import {
  fromZonedDateTimeLocalValue,
  type ActivityFeedEntry,
  type GroupPhoto,
} from '@lbc/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type TouchEvent } from 'react';

import { api } from '../../services/api/client';
import { PhotoViewer } from '../photos/PhotoViewer';

interface ActivityFeedProps {
  memberships: Array<{ id: string; groupId: string; groupName: string | null }>;
  onViewed?: (latestCreatedAt: string | undefined) => void;
}

interface FeedDay { key: string; label: string; entries: ActivityFeedEntry[]; }

const FALLBACK_TIMEZONE = 'Europe/Berlin';
const GROUP_COLORS = ['#f3ca59', '#a9d7ef', '#ff927e', '#c7b9e8', '#9edbd4', '#f49bb0'];
const OWN_FEED_COLOR = '#f3ca59';
const PULL_THRESHOLD = 56;
const NOOP_VIEWED = () => undefined;

export function ActivityFeed({ memberships, onViewed = NOOP_VIEWED }: ActivityFeedProps) {
  const timezone = viewerTimezone();
  const [daysBack, setDaysBack] = useState(0);
  const [entries, setEntries] = useState<ActivityFeedEntry[]>([]);
  const [latestCreatedAt, setLatestCreatedAt] = useState<string | undefined>();
  const [hasNew, setHasNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('all');
  const [selectedPhoto, setSelectedPhoto] = useState<GroupPhoto | null>(null);
  const [pullDistance, setPullDistance] = useState(0);
  const pullStart = useRef<number | null>(null);

  const range = useMemo(() => dayRange(timezone, daysBack), [daysBack, timezone]);
  const colorByGroup = useMemo(
    () => new Map(memberships.map((membership) => [membership.groupId, groupColor(membership.groupId)])),
    [memberships],
  );
  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await api.activityFeed(range.from, range.to);
      setEntries(result.entries);
      const latest = latestCreated(result.entries);
      setLatestCreatedAt(latest);
      setHasNew(false);
      onViewed(latest);
    } catch {
      setError('Der Feed konnte gerade nicht geladen werden.');
    } finally {
      setLoading(false);
      setPullDistance(0);
    }
  }, [onViewed, range.from, range.to]);

  // Initial data loading intentionally updates state from this effect.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (latestCreatedAt === undefined) return;
    const timer = window.setInterval(() => {
      void api.activityFeed(range.from, range.to, latestCreatedAt).then((result) => {
        if (result.hasNew) setHasNew(true);
      }).catch(() => undefined);
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [latestCreatedAt, range.from, range.to]);

  const visibleEntries = entries.filter(
    (entry) => selectedGroupId === 'all' || entry.event.groupId === selectedGroupId,
  );
  const days = groupByDay(visibleEntries, timezone);

  function handleTouchStart(event: TouchEvent<HTMLElement>) {
    if (window.scrollY <= 1) pullStart.current = event.touches[0]?.clientY ?? null;
  }
  function handleTouchMove(event: TouchEvent<HTMLElement>) {
    if (pullStart.current === null) return;
    const distance = Math.max(0, (event.touches[0]?.clientY ?? pullStart.current) - pullStart.current);
    setPullDistance(Math.min(72, distance * 0.55));
  }
  function handleTouchEnd() {
    const shouldRefresh = pullDistance >= PULL_THRESHOLD;
    pullStart.current = null;
    if (shouldRefresh) void refresh();
    else setPullDistance(0);
  }

  return (
    <section id="activity-feed" className="activity-feed" aria-labelledby="activity-feed-title" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      <div className={`activity-feed__pull ${pullDistance > 0 ? 'activity-feed__pull--visible' : ''}`} aria-live="polite">
        {loading ? 'Aktualisiere …' : pullDistance >= PULL_THRESHOLD ? 'Loslassen zum Aktualisieren' : 'Zum Aktualisieren ziehen'}
      </div>
      <h2 id="activity-feed-title" className="visually-hidden">Aktivitäten</h2>
      <div className="activity-feed__chips" role="group" aria-label="Gruppen filtern">
        <button className={`activity-feed__chip ${selectedGroupId === 'all' ? 'activity-feed__chip--active' : ''}`} type="button" aria-pressed={selectedGroupId === 'all'} onClick={() => setSelectedGroupId('all')}>Alle</button>
        {memberships.map((membership) => {
          const color = colorByGroup.get(membership.groupId) ?? '#f3ca59';
          const active = selectedGroupId === membership.groupId;
          return <button className={`activity-feed__chip ${active ? 'activity-feed__chip--active' : ''}`} type="button" key={membership.id} aria-pressed={active} style={groupStyle(color)} onClick={() => setSelectedGroupId(membership.groupId)}>{membership.groupName ?? 'Unbenannte Gruppe'}</button>;
        })}
      </div>
      {hasNew ? <button className="activity-feed__new" type="button" onClick={() => void refresh()}>✦ Neue Momente anzeigen</button> : null}
      {loading && entries.length === 0 ? <p className="chart-empty">Feed wird geladen …</p> : null}
      {error !== '' ? <p className="form-error" role="alert">{error}</p> : null}
      {!loading && error === '' && visibleEntries.length === 0 ? <p className="chart-empty">Für diesen Tag gibt es noch keine Aktivitäten.</p> : null}
      <ol className="activity-feed__list">
        {days.map((day) => (
          <li className="activity-feed__day" key={day.key}>
            <p><span>{day.label}</span></p>
            <ol>
              {day.entries.map((entry, index) => {
                const color = entry.isOwn ? OWN_FEED_COLOR : (colorByGroup.get(entry.event.groupId) ?? groupColor(entry.event.groupId));
                return <li className="activity-feed__entry" key={entry.event.id}>
                  <time dateTime={entry.event.occurredAt}>{formatTime(entry.event.occurredAt, timezone)}</time>
                  <span className={`activity-feed__rail ${index === day.entries.length - 1 ? 'activity-feed__rail--last' : ''}`} style={groupStyle(color)} aria-hidden="true"><i /></span>
                  <article>
                    <div className="activity-feed__entry-heading">
                      <strong>{entry.isOwn ? 'Du' : entry.participantName}</strong>
                      {entry.isOwn ? null : <span className="activity-feed__badge" style={groupStyle(color)}>{entry.groupName ?? 'Unbenannte Gruppe'}</span>}
                    </div>
                    <p>{entry.isOwn ? formatOwnDrink(entry) : formatDrink(entry)}</p>
                    {entry.photo !== null ? <FeedPhotoThumbnail photo={entry.photo} onOpen={() => setSelectedPhoto(entry.photo)} /> : null}
                  </article>
                </li>;
              })}
            </ol>
          </li>
        ))}
      </ol>
      <button className="activity-feed__older" type="button" onClick={() => setDaysBack((current) => current + 1)} disabled={loading}>
        {daysBack === 0 ? 'Gestern laden' : 'Einen weiteren Tag laden'}
      </button>
      <button className="visually-hidden activity-feed__keyboard-refresh" type="button" onClick={() => void refresh()}>Feed aktualisieren</button>
      {selectedPhoto !== null ? <PhotoViewer key={selectedPhoto.id} photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} /> : null}
    </section>
  );
}

function FeedPhotoThumbnail({ photo, onOpen }: { photo: GroupPhoto; onOpen: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    void api.photoContent(photo.id).then((content) => {
      objectUrl = URL.createObjectURL(content);
      if (active) setUrl(objectUrl);
      else URL.revokeObjectURL(objectUrl);
    }).catch(() => active && setFailed(true));
    return () => { active = false; if (objectUrl !== null) URL.revokeObjectURL(objectUrl); };
  }, [photo.id]);
  return <button className="activity-feed__thumbnail" type="button" onClick={onOpen} aria-label="Foto groß ansehen">
    {url !== null ? <img src={url} alt={`LBC-Moment von ${photo.photographerName}`} loading="lazy" decoding="async" /> : <span>{failed ? 'Foto konnte nicht geladen werden' : 'Foto wird geladen …'}</span>}
    <small>♥ {photo.likeCount}</small>
  </button>;
}

function groupByDay(entries: ActivityFeedEntry[], timezone: string): FeedDay[] {
  const grouped = new Map<string, ActivityFeedEntry[]>();
  entries.forEach((entry) => {
    const key = localDateKey(entry.event.occurredAt, timezone);
    grouped.set(key, [...(grouped.get(key) ?? []), entry]);
  });
  return [...grouped.entries()].map(([key, dayEntries]) => ({ key, entries: dayEntries, label: dayLabel(key, timezone) }));
}
function viewerTimezone(): string { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE; } catch { return FALLBACK_TIMEZONE; } }
function dayRange(timezone: string, daysBack: number): { from: string; to: string } {
  const today = localDateKey(new Date().toISOString(), timezone);
  const start = new Date(`${today}T00:00:00Z`); start.setUTCDate(start.getUTCDate() - daysBack);
  const end = new Date(`${today}T00:00:00Z`); end.setUTCDate(end.getUTCDate() + 1);
  return { from: fromZonedDateTimeLocalValue(`${start.toISOString().slice(0, 10)}T00:00`, timezone).toISOString(), to: new Date(fromZonedDateTimeLocalValue(`${end.toISOString().slice(0, 10)}T00:00`, timezone).getTime() - 1).toISOString() };
}
function localDateKey(value: string, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(value));
  const fields = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  return `${fields.year}-${fields.month}-${fields.day}`;
}
function dayLabel(key: string, timezone: string): string {
  const today = localDateKey(new Date().toISOString(), timezone);
  const yesterday = new Date(`${today}T00:00:00Z`); yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  const formatted = new Intl.DateTimeFormat('de-DE', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(`${key}T12:00:00Z`));
  if (key === today) return `Heute · ${formatted}`;
  if (key === yesterday.toISOString().slice(0, 10)) return `Gestern · ${formatted}`;
  return formatted;
}
function latestCreated(entries: ActivityFeedEntry[]): string | undefined { return entries.reduce<string | undefined>((latest, entry) => latest === undefined || entry.event.createdAt > latest ? entry.event.createdAt : latest, undefined); }
function formatTime(occurredAt: string, timezone: string): string { return new Intl.DateTimeFormat('de-DE', { timeZone: timezone, hour: '2-digit', minute: '2-digit' }).format(new Date(occurredAt)); }
function formatDrink(entry: ActivityFeedEntry): string { return `${entry.event.volumeMl.toLocaleString('de-DE')} ml · ${entry.event.abv.toLocaleString('de-DE')} % vol.`; }
function formatOwnDrink(entry: ActivityFeedEntry): string { return `Du hast ${entry.event.volumeMl.toLocaleString('de-DE')} ml · ${entry.event.abv.toLocaleString('de-DE')} % vol. getrunken.`; }
function groupColor(groupId: string): string { return GROUP_COLORS[[...groupId].reduce((value, character) => ((value * 31) + character.charCodeAt(0)) >>> 0, 0) % GROUP_COLORS.length] ?? '#f3ca59'; }
function groupStyle(color: string): CSSProperties { return { '--feed-group-color': color } as CSSProperties; }
